/* eslint-disable @typescript-eslint/no-this-alias */
import { CognitoAuth } from 'amazon-cognito-auth-js';
import * as AmazonCognitoIdentity from 'amazon-cognito-identity-js';
import { environment } from '../../environments/environment';
import { Location, DOCUMENT } from '@angular/common';
import { Inject, Injectable, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { LoaderService } from './loader.service';
import { LocalStorageService } from './localStorage.service';
import { NoopStorage } from './noop-storage';
import jwtDecode from 'jwt-decode';
import { last } from 'rxjs';

@Injectable()
export class CognitoService implements OnDestroy {
  cookieName = 'devhub_tkn';
  cookiePath = '/';
  cookieSecure = true;
  cookieSameSite = 'strict';
  cookieExpiresDays = 365;

  constructor(
    private loaderService: LoaderService,
    private location: Location,
    private router: Router,
    @Inject(DOCUMENT) private document: Document,
    private localStorageService: LocalStorageService
  ) {
    // Session handling across multiple tabs
    window.addEventListener('storage', (event) => {
      if (event.key === 'isLoggedIn') {
        const isLoggedIn = localStorage.getItem('isLoggedIn');
        if (isLoggedIn === 'false') {
          this.router.navigate(['/']).then(() => {
            window.location.reload();
          });
        }
      }
    });
  }

  envName = () => {
    const host = window.location.host;
    if (host.includes('localhost')) {
      return 'localhost';
    } else {
      return host;
    }
  };

  poolData = {
    UserPoolId: environment.UserPoolId,
    ClientId: environment.ClientId,
    Storage: new NoopStorage(),
  };

  authData = {
    UserPoolId: environment.UserPoolId,
    AppWebDomain: environment.AppWebDomain,
    ClientId: environment.ClientId,
    RedirectUriSignIn: environment.RedirectUriSignIn,
    RedirectUriSignOut: environment.RedirectUriSignOut,
    TokenScopesArray: environment.TokenScopesArray,
    Storage: new NoopStorage(),
  };

  userPool = new AmazonCognitoIdentity.CognitoUserPool(this.poolData);
  auth = new CognitoAuth(this.authData);
  cognitoUser = null;
  jwt: any;
  idJwt: any;

  ngOnDestroy() {
    window.removeEventListener('storage', () => {}, false);
  }

  getUserInfo() {
    return this.userPool.getCurrentUser();
  }

  getJWT() {
    return this.jwt;
  }

  getIdJWT() {
    return this.idJwt;
  }

  logout() {
    console.log('hard logout....');
    this.loaderService.loadingContent.emit(true);
    try {
      if (this.auth) {
        this.auth.signOut();
      }
    } catch (err) {
      // console.log(err);
    }
    this.cognitoUser = null;
    this.jwt = null;
    this.auth = null;
    this.clearUserCookie();
    this.localStorageService.removeItem('groups');
    this.localStorageService.setItem('isLoggedIn', false);
    this.localStorageService.removeItem('user');
  }

  createSession() {
    this.cognitoUser = this.userPool.getCurrentUser();
    if (this.cognitoUser !== null) {
      const that = this;
      this.cognitoUser.getSession(function (err: any, session: any) {
        if (err) {
          that.cognitoWebResponse();
        } else {
          that.jwt = session.getAccessToken().getJwtToken();
          that.idJwt = session.getIdToken().getJwtToken();
          that.setUserCookie();
          that.setUserStorage(that.idJwt);
          that.localStorageService.setItem('isLoggedIn', true);
        }
      });
    } else {
      // Failsafe if congito user does not exist.
      this.cognitoWebResponse();
    }
  }

  private clearUserCookie() {
    document.cookie = `${this.cookieName}=; path=${this.cookiePath}; expires=Thu, 01 Jan 1970 00:00:00 UTC; ${
      this.cookieSecure ? 'Secure;' : ''
    } SameSite=${this.cookieSameSite}`;
  }

  private setUserCookie() {
    if (this.jwt) {
      const cookieExpiryString = new Date(
        new Date().getTime() + this.cookieExpiresDays * 24 * 60 * 60 * 1000
      ).toUTCString();

      const cookie = `${this.cookieName}=${encodeURIComponent(this.jwt)}; path=${this.cookiePath};${
        this.cookieSecure ? ' Secure;' : ''
      } samesite=${this.cookieSameSite}; expires=${cookieExpiryString}`;
      document.cookie = cookie;
    }
  }

  setUserStorage(token: any) {
    const user = {
      username: jwtDecode(token)['sub'] ? jwtDecode(token)['sub'] : '',
      email: jwtDecode(token)['email'] ? jwtDecode(token)['email'] : '',
      firstName: jwtDecode(token)['name'] ? jwtDecode(token)['name'] : '',
      lastName: jwtDecode(token)['family_name'] ? jwtDecode(token)['family_name'] : '',
      isDevhubUser: false,
    };
    this.localStorageService.setItem('user', JSON.stringify(user));
    return user;
  }

  cognitoWebResponse() {
    try {
      let curUrl = window.location.href;
      this.auth.parseCognitoWebResponse(curUrl);
      curUrl = curUrl.replace('#', '#?');
      this.jwt = this.auth.signInUserSession.accessToken.jwtToken;
      this.idJwt = this.auth.signInUserSession.idToken.jwtToken;
      this.setUserCookie();
      this.setUserStorage(this.idJwt);
      this.cognitoUser = this.userPool.getCurrentUser();
      this.localStorageService.setItem('isLoggedIn', true);
    } catch (err) {
      if (this.auth.signInUserSession.accessToken.jwtToken !== '') {
        this.jwt = this.auth.signInUserSession.accessToken.jwtToken;
        this.idJwt = this.auth.signInUserSession.idToken.jwtToken;
        this.setUserCookie();
        this.setUserStorage(this.idJwt);
        this.cognitoUser = this.userPool.getCurrentUser();
        this.localStorageService.setItem('isLoggedIn', true);
      }
    }
  }
}